import { wrap, proxy } from "comlink";

import { WebTerminal } from "./web-terminal";
import { VM } from "./vm";
import { download, readFiles, stringToBinary, binaryToString } from "./utils";
import { Storage } from "./storage";

// avoid vite complaining about toplevel await
(async () => {
  const storage = new Storage<Uint8Array>("local");
  const terminal = new WebTerminal("#terminal");

  // WebWorker VM creation
  const RemoteVM = wrap<typeof VM>(
    new Worker(new URL("./comlink-vm", import.meta.url), {
      type: "module",
    }),
  );

  // start the vm
  const vm = await new RemoteVM(proxy(terminal));

  // wait for boot
  await vm.boot();

  // restore home
  let restored = false;

  // from url parameter
  if (!restored) {
    const params = new URLSearchParams(window.location.search);
    const home = await stringToBinary(params.get("home"));
    if (home != null)
      try {
        await vm.restoreHome(home);
        restored = true;
      } catch (e: any) {
        console.error("Cannot restore home from URL parameter", e);
      }
  }

  // from storage
  if (!restored) {
    const home = await storage.get("home");
    try {
      await vm.restoreHome(home);
      restored = true;
    } catch (e: any) {
      console.error("Cannot restore home from storage", e);
    }
  }

  // hide loader
  document.getElementById("loader")!.style.display = "none";

  terminal.setVM(vm);

  // minimum delay between two checkpoints (ms)
  const checkpointInterval = 10 * 1000;
  let lastCheckpoint = performance.now();
  // checkpoint at each modification but not more than once every checkpointInterval
  const callback = async (payload: any) => {
    const t = performance.now();
    if (!payload.path.startsWith("/home/user/")) return;
    if (t - lastCheckpoint > checkpointInterval) {
      lastCheckpoint = t;
      const data = await vm.saveHome();
      await storage.set("home", data);
      console.log(
        `vm checkpoint done at ${new Date().toLocaleString()} in ${performance.now() - t}ms`,
      );
    } else {
      // too early, wait for the next checkpoint
      const previousCheckpoint = lastCheckpoint;
      setTimeout(
        // checkpoint only if no other checkpoint has been done since the last one
        () =>
          lastCheckpoint === previousCheckpoint ? callback(payload) : null,
        // one second after the planned checkpoint
        checkpointInterval - (t - lastCheckpoint) + 1 * 1000,
      );
    }
  };
  await vm.addEventListener("fs.modified", proxy(callback));

  // connect buttons
  document.getElementById("restart")!.addEventListener("click", async () => {
    await storage.remove("home");
    const url = new URL(window.location.href);
    url.searchParams.delete("home");
    window.location.href = url.href;
  });

  document.getElementById("download")!.addEventListener("click", async () => {
    let path = window.prompt("Chemin du fichier à récupérer") ?? "";
    if (path === "") return;
    if (!path.startsWith("/")) path = `/home/user/${path}`;
    await download(await vm.readFile(path), path.split("/").pop()!);
  });

  document.getElementById("upload")!.addEventListener("click", async () => {
    await readFiles(async (content: Uint8Array, filename: string) =>
      vm.createFile(content, `/home/user/${filename}`),
    );
  });

  document.getElementById("share")!.addEventListener("click", async () => {
    const home = await vm.saveHome();
    if (home == null) return;
    const url = new URL(window.location.href);
    const param = await binaryToString(home);
    if (param == null) return;
    url.searchParams.set("home", param);
    await navigator.clipboard.writeText(url.href);
    alert("L'URL a été copiée dans le presse-papier");
  });

  document.getElementById("info")!.addEventListener("click", async () => {
    alert(JSON.stringify(await vm.info(), undefined, 2));
  });

  terminal.focus();
})();
